import React from 'react';
import PropTypes from 'prop-types';
import { createGenerateClassName, jssPreset } from '@material-ui/core/styles';
import { create } from 'jss';
import { FormattedMessage } from 'react-intl';
import { updateIntl } from 'react-intl-redux';
import { IntlProvider, createIntlCache} from 'react-intl';
import getMessages from '../lib/i18n/getMessages';
import JssProvider from 'react-jss/lib/JssProvider';
import { ApolloProvider } from 'react-apollo';
import client from '../lib/apolloClient';
import { get } from 'lodash';
import gql from 'graphql-tag';
import { Query } from 'react-apollo';
import NotificationDropDown from '../components/Notifications/NotificationDropDown';
import CssBaseline from '@material-ui/core/CssBaseline';
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import themes from '../lib/themes';

// Import the new Succession Notifications Container
import SuccessionNotificationsContainer from '../components/Succession/Notifications/containers/SuccessionNotificationsContainer';

const cache = createIntlCache();

const setIntl = () => {
  updateIntl(
  {
    locale   : 'en',
    messages : getMessages(window.locale || 'en'),
  },
  cache
);}

const generateClassName = createGenerateClassName({
  dangerouslyUseGlobalCSS : true,
  productionPrefix        : 'fbNot',
});

export const getCurrentUser = gql`
  query getCurrentUser($after: String) {
    getCurrentUser {
      user_name
      open_notifications_count
      notifications(first: 10, after: $after) {
        edges {
          node {
            id
            action
            notifiableType
            createdAt
            readAt
            data
            notifiable {
              __typename
              ... on BusinessContinuityPlan {
                __typename
                id
              }
              ... on PersonalMessage {
                id
                __typename
                conversation {
                  id
                  conversationType
                }
              }
              ... on EducationalContent {
                __typename
                id
                title
              }
              ... on PartialSaleListingUser {
                __typename
                id
                partial_sale_listing {
                  id
                  segment_details
                }
              }
              ... on VectorDocument {
                __typename
                id
                title
              }
            }
            actor {
              allowed_name
              user_name
              profile_picture
            }
            recipient {
              user_name
            }
          }
          cursor
        }
        pageInfo {
          hasPreviousPage
          hasNextPage
          endCursor
          startCursor
        }
      }
    }
  }
`;

class NotificationsMenuContainer extends React.Component {
  state = { notifications: [], openCount: 0, after: null, loaded: false };

  // Helper method to detect if findbob_modernization feature is enabled
  isModernizationEnabled = () => {
    const { features } = this.props;
    const isEnabled = features && Array.isArray(features) && features.includes('findbob_modernization');
    
    return isEnabled;
  };

  renderLegacyNotifications = () => {
    const jss = create(jssPreset());
    const { notifications, openCount, after, hasNextPage } = this.state;
    const { inMenu, color, theme_name } = this.props;
    let theme = themes[theme_name];
    setIntl();
    
    return (
      <IntlProvider messages={getMessages(window.locale || 'en')} locale={window.locale}>
        <ApolloProvider client={client}>
          <JssProvider jss={jss} generateClassName={generateClassName} classNamePrefix="fbNot-">
            <MuiThemeProvider theme={createMuiTheme(theme)}>
              <CssBaseline />
              <Query
                query={getCurrentUser}
                variables={{ after }}
                fetchPolicy="no-cache"
                onCompleted={data => {
                  if (after !== null && !data.loadMore) return;
                  this.setState({
                    notifications : [
                      ...notifications,
                      ...get(data, 'getCurrentUser.notifications.edges', []).map(n => n.node),
                    ],
                    openCount     : get(data, 'getCurrentUser.open_notifications_count'),
                    after         : get(data, 'getCurrentUser.notifications.pageInfo.endCursor', ''),
                    hasNextPage   : get(data, 'getCurrentUser.notifications.pageInfo.hasNextPage', false),
                  });
                }}>
                {({ fetchMore }) => {
                  return (
                    <NotificationDropDown
                      inMenu={inMenu}
                      color={color}
                      notifications={notifications}
                      openCount={openCount}
                      updateCount={number => {
                        this.setState({ openCount: openCount - number });
                      }}
                      after={after}
                      fetchMore={fetchMore}
                      resetLoaded={s => {
                        this.setState({ ...s });
                      }}
                      hasNextPage={hasNextPage}
                    />
                  );
                }}
              </Query>
            </MuiThemeProvider>
          </JssProvider>
        </ApolloProvider>
      </IntlProvider>
    );
  };

  renderSuccessionNotifications = () => {
    // Safety check - only render if we have proper feature flag setup
    if (!this.isModernizationEnabled()) {
      return this.renderLegacyNotifications();
    }

    return (
      <IntlProvider messages={translations[window.locale || 'en']} locale={window.locale || defaultLocale}>
        <ApolloProvider client={client}>
          <SuccessionNotificationsContainer {...this.props} />
        </ApolloProvider>
      </IntlProvider>
    );
  };

  render() {
    try {
      // Check feature flag and render appropriate component
      if (this.isModernizationEnabled()) {
        return this.renderSuccessionNotifications();
      } else {
        return this.renderLegacyNotifications();
      }
    } catch (error) {
      console.error('Error in NotificationsMenuContainer render:', error);
      // Fallback to legacy notifications if there's any error
      return this.renderLegacyNotifications();
    }
  }
}

NotificationsMenuContainer.propTypes = {
  inMenu : PropTypes.bool,
  color  : PropTypes.string,
  features: PropTypes.arrayOf(PropTypes.string), // Add features prop
  theme_name: PropTypes.string,
};

NotificationsMenuContainer.defaultProps = {
  inMenu : false,
  color  : 'primary',
  features: [], // Default to empty array
  theme_name: 'default',
};

const NotificationsMenuContainerWithContext = (props, railsContext) => {
  // Extract features from railsContext and pass them as props
  // Provide defaults to handle cases where railsContext is not provided
  const features = railsContext?.features || [];
  const themeName = railsContext?.theme_name || 'default';
  
  return (
    <NotificationsMenuContainer 
      {...props} 
      theme_name={themeName}
      features={features}
    />
  );
};

export default NotificationsMenuContainerWithContext;
